/**
 * Class Management Routes
 * Routes for classes, campuses, enrollments, and applications
 */

import { lazy } from "react";
import { RouteObject } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";
import { LazyPage } from "../helpers";

// Classes
const ClassesListPage = lazy(() => import("@/pages/classes/ClassesListPage").then(m => ({ default: m.ClassesListPage })));
const ClassFormPage = lazy(() => import("@/pages/classes/ClassFormPage").then(m => ({ default: m.ClassFormPage })));
const ClassDetailPage = lazy(() => import("@/pages/classes/ClassDetailPage").then(m => ({ default: m.ClassDetailPage })));
const ClassPlacementPage = lazy(() => import("@/pages/classes/ClassPlacementPage").then(m => ({ default: m.ClassPlacementPage })));
const ClassRosterPage = lazy(() => import("@/pages/classes/ClassRosterPage").then(m => ({ default: m.ClassRosterPage })));
const CloneClassPage = lazy(() => import("@/pages/classes/CloneClassPage").then(m => ({ default: m.CloneClassPage })));
const ClassTemplatesPage = lazy(() => import("@/pages/classes/ClassTemplatesPage").then(m => ({ default: m.ClassTemplatesPage })));
const ClassLessonProgressPage = lazy(() => import("@/pages/classes/ClassLessonProgressPage").then(m => ({ default: m.ClassLessonProgressPage })));
const ClassAssignmentsPage = lazy(() => import("@/pages/classes/ClassAssignmentsPage").then(m => ({ default: m.ClassAssignmentsPage })));
const ClassGradebookPage = lazy(() => import("@/pages/classes/ClassGradebookPage").then(m => ({ default: m.ClassGradebookPage })));
const ClassAssessmentsPage = lazy(() => import("@/pages/classes/ClassAssessmentsPage").then(m => ({ default: m.ClassAssessmentsPage })));
const ClassSchedulesPage = lazy(() => import("@/pages/attendance").then(m => ({ default: m.ClassSchedulesPage })));
const LessonProgressHubPage = lazy(() => import("@/pages/classes/LessonProgressHubPage").then(m => ({ default: m.LessonProgressHubPage })));
const AssignmentsHubPage = lazy(() => import("@/pages/classes/AssignmentsHubPage").then(m => ({ default: m.AssignmentsHubPage })));

// Campuses
const CampusesListPage = lazy(() => import("@/pages/campuses/CampusesListPage").then(m => ({ default: m.CampusesListPage })));
const CampusFormPage = lazy(() => import("@/pages/campuses/CampusFormPage").then(m => ({ default: m.CampusFormPage })));
const CampusDetailPage = lazy(() => import("@/pages/campuses/CampusDetailPage"));

// Applications
const ApplicationsListPage = lazy(() => import("@/pages/applications/ApplicationsListPage"));
const ApplicationFormPage = lazy(() => import("@/pages/applications/ApplicationFormPage"));
const ApplicationDetailPage = lazy(() => import("@/pages/applications/ApplicationDetailPage"));

// Enrollments
const EnrollmentsListPage = lazy(() => import("@/pages/enrollments/EnrollmentsListPage"));
const EnrollmentFormPage = lazy(() => import("@/pages/enrollments/EnrollmentFormPage"));
const EnrollmentDetailPage = lazy(() => import("@/pages/enrollments/EnrollmentDetailPage"));
const PausedEnrollmentsPage = lazy(() => import("@/pages/enrollments/PausedEnrollmentsPage"));

// Creating, editing and cloning a class are admin/principal/staff writes on
// the classes API (manage:class-setup). Teachers hold manage:classes only so
// they can open a class page from their portal.
export const classRoutes: RouteObject[] = [
  // Class Routes
  {
    path: "/classes",
    element: (
      <ProtectedRoute permission="manage:classes">
        <AppShell>
          <LazyPage><ClassesListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/new",
    element: (
      <ProtectedRoute permission="manage:class-setup" deniedRedirect="home">
        <AppShell>
          <LazyPage><ClassFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/templates",
    element: (
      <ProtectedRoute permission="manage:classes">
        <AppShell>
          <LazyPage><ClassTemplatesPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/:id/edit",
    element: (
      <ProtectedRoute permission="manage:class-setup" deniedRedirect="home">
        <AppShell>
          <LazyPage><ClassFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/:id",
    element: (
      <ProtectedRoute permission="manage:classes">
        <AppShell>
          <LazyPage><ClassDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/clone",
    element: (
      <ProtectedRoute permission="manage:class-setup" deniedRedirect="home">
        <AppShell>
          <LazyPage><CloneClassPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/:sourceId/clone",
    element: (
      <ProtectedRoute permission="manage:class-setup" deniedRedirect="home">
        <AppShell>
          <LazyPage><CloneClassPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    // A class roster is a school-wide listing of other families' children,
    // so gate it on staff roles — NOT view:enrollments, which parents hold
    // for their OWN-enrollment views and would let any parent open any
    // class's full roster (PII leak). Mirrors the placement route below.
    path: "/classes/:classId/roster",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal", "staff", "teacher"]}>
        <AppShell>
          <LazyPage><ClassRosterPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/:id/placement",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal", "staff", "teacher"]}>
        <AppShell>
          <LazyPage><ClassPlacementPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/lesson-progress",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal", "staff", "teacher"]}>
        <AppShell>
          <LazyPage><LessonProgressHubPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/assignments",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal", "staff", "teacher"]}>
        <AppShell>
          <LazyPage><AssignmentsHubPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/:classId/lesson-progress",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal", "staff", "teacher"]}>
        <AppShell>
          <LazyPage><ClassLessonProgressPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/:classId/assignments",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal", "staff", "teacher"]}>
        <AppShell>
          <LazyPage><ClassAssignmentsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/:classId/gradebook",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal", "staff", "teacher"]}>
        <AppShell>
          <LazyPage><ClassGradebookPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/:classId/assessments",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal", "staff", "teacher"]}>
        <AppShell>
          <LazyPage><ClassAssessmentsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/classes/:classId/schedules",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal", "staff", "teacher"]}>
        <AppShell>
          <LazyPage><ClassSchedulesPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Campus Routes
  {
    path: "/campuses",
    element: (
      <ProtectedRoute permission="manage:campuses">
        <AppShell>
          <LazyPage><CampusesListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/campuses/new",
    element: (
      <ProtectedRoute permission="manage:campuses">
        <AppShell>
          <LazyPage><CampusFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/campuses/:id/edit",
    element: (
      <ProtectedRoute permission="manage:campuses">
        <AppShell>
          <LazyPage><CampusFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/campuses/:id",
    element: (
      <ProtectedRoute permission="manage:campuses">
        <AppShell>
          <LazyPage><CampusDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Application Routes
  {
    path: "/applications",
    element: (
      <ProtectedRoute permission="manage:applications">
        <AppShell>
          <LazyPage><ApplicationsListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/applications/new",
    element: (
      <ProtectedRoute permission="manage:applications">
        <AppShell>
          <LazyPage><ApplicationFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/applications/:applicationId",
    element: (
      <ProtectedRoute permission="manage:applications">
        <AppShell>
          <LazyPage><ApplicationDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Enrollment Routes
  {
    path: "/enrollments",
    element: (
      <ProtectedRoute permission="manage:enrollments">
        <AppShell>
          <LazyPage><EnrollmentsListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/enrollments/new",
    element: (
      <ProtectedRoute permission="manage:enrollments">
        <AppShell>
          <LazyPage><EnrollmentFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/enrollments/:enrollmentId",
    element: (
      <ProtectedRoute permission="manage:enrollments">
        <AppShell>
          <LazyPage><EnrollmentDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/enrollments/paused",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal"]}>
        <AppShell>
          <LazyPage><PausedEnrollmentsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
];
